"use client";

import { useEffect, useState } from "react";
import dynamic from "next/dynamic";

/**
 * DeferredSplineBackground — wraps SplineBackground in two layers of
 * deferral so it doesn't dominate the LCP path:
 *
 *   1. `dynamic(... { ssr: false })` keeps Spline + its WebGL runtime
 *      out of the server-rendered HTML and the initial JS bundle —
 *      the loader chunk only ships once we actually render this.
 *   2. The wrapper itself waits one `requestIdleCallback` cycle after
 *      mount before flipping the render flag. That gives the hero
 *      LCP image a clear runway to paint before Three.js + the
 *      scene.splinecode download kick off.
 *
 * Net effect: the page paints first, then ~200ms later the blob
 * backdrop fades in. UX is identical for any real user; Lighthouse
 * gets clean numbers.
 */

const SplineBackground = dynamic(() => import("./SplineBackground"), {
  ssr: false,
  loading: () => null,
});

export default function DeferredSplineBackground() {
  const [mount, setMount] = useState(false);

  useEffect(() => {
    // Pull the API off `window` once and via `unknown` so TS narrows
    // each path cleanly — the `in` operator narrows `window` to `never`
    // when both branches are reached otherwise.
    const w = window as unknown as {
      requestIdleCallback?: (
        cb: () => void,
        opts?: { timeout: number },
      ) => number;
      cancelIdleCallback?: (id: number) => void;
    };
    let handle: number;
    if (typeof w.requestIdleCallback === "function") {
      handle = w.requestIdleCallback(() => setMount(true), { timeout: 1500 });
    } else {
      handle = window.setTimeout(() => setMount(true), 600);
    }
    return () => {
      if (typeof w.cancelIdleCallback === "function") {
        w.cancelIdleCallback(handle);
      } else {
        clearTimeout(handle);
      }
    };
  }, []);

  if (!mount) return null;
  return <SplineBackground />;
}
